/* =========================================================================
   wordmark.tsx — the TENURE wordmark: "TENU" in foreground ink, "RE" in a
   worn-brass finish, with a classic long-shadow extrusion cast behind the
   letters (a dense stack of incrementally-offset text-shadow layers, NOT a
   detached mirrored copy — the shadow starts at the glyph with no gap).
   Pure CSS/SVG (styles in public/assets/tenure.css) — no raster assets.

   Sizes:
     sm — sidebar brand (~52px brand-mark box, shadow visible, not clipped)
     md — estate access gate
     lg — auth gate hero (pair with `tagline` for the CFO-tone strapline)
   ========================================================================= */

export type WordmarkSize = "sm" | "md" | "lg";

/** Per-size long-shadow geometry: layer count and per-layer px step (at
 *  --wm-shadow-scale: 1 — see tenure.css for the per-size scale knob). */
const SHADOW_GEOMETRY: Record<
  WordmarkSize,
  { layers: number; xStep: number; yStep: number }
> = {
  sm: { layers: 12, xStep: 0.3, yStep: 1.1 },
  md: { layers: 20, xStep: 0.34, yStep: 1.6 },
  lg: { layers: 30, xStep: 0.4, yStep: 2.3 },
};

/** Ease-out quad: fast initial change, leveling off — used to decay the
 *  shadow layers' opacity so the extrusion reads as a dense, quickly-fading
 *  near edge and a long, faint tail (not a linear ramp). */
function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

/** Build one stacked-layer text-shadow value: `n` incrementally-offset
 *  copies of the glyph, alpha decaying from `startAlpha` to `endAlpha`. Each
 *  offset is wrapped in calc(...* var(--wm-shadow-scale)) so a single CSS
 *  variable can further shrink/grow the whole stack per size without
 *  recomputing this string. */
function shadowLayerStack(
  n: number,
  colorVar: string,
  startAlpha: number,
  endAlpha: number,
  xStep: number,
  yStep: number,
): string {
  const layers: string[] = [];
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const alpha = startAlpha + (endAlpha - startAlpha) * easeOutQuad(t);
    const x = (i * xStep).toFixed(2);
    const y = (i * yStep).toFixed(2);
    layers.push(
      `calc(${x}px * var(--wm-shadow-scale, 1)) calc(${y}px * var(--wm-shadow-scale, 1)) 0 hsl(var(${colorVar}) / ${alpha.toFixed(3)})`,
    );
  }
  return layers.join(", ");
}

/** The full long-shadow value for a given size: the ink extrusion (drawn on
 *  top, near-black and dense near the glyph, fading to a long faint tail),
 *  a soft blurred contact shadow past the last hard layer, and — behind
 *  both — a second, very faint foreground-tinted extrusion slightly further
 *  offset so the tall silhouette stays legible against a dark card (a
 *  "light from above" rim; harmless on a light card, where it reads as a
 *  near-black tint that just reinforces the ink stack). */
function buildLongShadow(size: WordmarkSize): string {
  const { layers, xStep, yStep } = SHADOW_GEOMETRY[size];
  const ink = shadowLayerStack(
    layers,
    "--wm-shadow-ink",
    0.55,
    0.04,
    xStep,
    yStep,
  );
  const lastY = layers * yStep;
  const blur = `calc(${(xStep * layers).toFixed(2)}px * var(--wm-shadow-scale, 1)) calc(${(lastY * 1.12).toFixed(2)}px * var(--wm-shadow-scale, 1)) 14px hsl(var(--wm-shadow-ink) / 0.25)`;
  const backer = shadowLayerStack(
    layers,
    "--c-foreground",
    0.09,
    0.015,
    xStep,
    yStep * 1.1,
  );
  return [ink, blur, backer].join(", ");
}

export function Wordmark({
  size = "md",
  tagline = false,
}: Readonly<{ size?: WordmarkSize; tagline?: boolean }>) {
  const shadowStyle = {
    "--wm-shadow-stack": buildLongShadow(size),
  } as React.CSSProperties;

  return (
    <span className={`wordmark wordmark-${size}`} style={shadowStyle}>
      <span className="wm-row" role="img" aria-label="TENURE">
        <span className="wm-tenu" data-text="TENU" aria-hidden="true">
          TENU
        </span>
        <span className="wm-re" data-text="RE" aria-hidden="true">
          RE
        </span>
      </span>
      {tagline && (
        <span className="wm-tagline">Long-term occupancy, modelled.</span>
      )}
    </span>
  );
}
